import {gql} from '@apollo/client';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {RoutineMemoryPageQuery, RoutineMemoryPageQueryVariables} from './__generated/RoutineMemoryPageQuery';
import {
  RoutineMemoryPagesQuery,
  RoutineMemoryPagesQueryVariables,
} from './__generated/RoutineMemoryPagesQuery';

export const ROUTINE_MEMORY_PAGES_QUERY = gql`
  query RoutineMemoryPagesQuery($routineId: StrictString!) @engComponent(name: LLM) @skipAudit {
    routineMemoryPages(routineId: $routineId) {
      pages {
        page
      }
      totalCount
      truncated
    }
  }
`;

export const ROUTINE_MEMORY_PAGE_QUERY = gql`
  query RoutineMemoryPageQuery($routineId: StrictString!, $page: StrictString!)
  @engComponent(name: LLM)
  @skipAudit {
    routineMemoryPage(routineId: $routineId, page: $page) {
      page
      content
    }
  }
`;

interface MemoryViewerPanelProps {
  agentName: string;
  onClose: () => void;
  routineId: string;
}

const MEMORY_VIEWER_PANEL_TITLE_ID = 'agent-memory-viewer-title';

const useStyles = createStyles((theme) => ({
  body: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  content: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'auto',
    padding: theme.spacing.lg,
  },
  contentPath: {
    overflowWrap: 'anywhere',
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing.md,
  },
  pageButton: {
    fontWeight: 400,
    height: 'auto',
    overflowWrap: 'anywhere',
    paddingBottom: theme.spacing.xs,
    paddingTop: theme.spacing.xs,
    textAlign: 'left',
    width: '100%',
  },
  pageButtonInner: {
    justifyContent: 'flex-start',
  },
  pageButtonLabel: {
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  },
  pageList: {
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    flex: '0 0 240px',
    overflowY: 'auto',
    padding: theme.spacing.md,
  },
  pageListControls: {
    backgroundColor: theme.white,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
}));

export default function MemoryViewerPanel({
  agentName,
  onClose,
  routineId,
}: MemoryViewerPanelProps): JSX.Element {
  const {classes} = useStyles();
  const [filter, setFilter] = useState('');
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const pagesResult = useSkippableBenchlingQuery<RoutineMemoryPagesQuery, RoutineMemoryPagesQueryVariables>(
    ROUTINE_MEMORY_PAGES_QUERY,
    {
      variables: {routineId},
      skip: false,
      fetchPolicy: 'network-only',
    }
  );
  const pages = useMemo(
    () => (pagesResult.type === QueryResultType.SUCCESS ? pagesResult.data.routineMemoryPages.pages : []),
    [pagesResult]
  );
  const totalCount =
    pagesResult.type === QueryResultType.SUCCESS ? pagesResult.data.routineMemoryPages.totalCount : 0;
  const truncated =
    pagesResult.type === QueryResultType.SUCCESS ? pagesResult.data.routineMemoryPages.truncated : false;
  const filteredPages = useMemo(() => {
    const normalizedFilter = filter.toLowerCase();
    return pages.filter((memoryPage) => memoryPage.page.toLowerCase().includes(normalizedFilter));
  }, [filter, pages]);
  const loadedCount = pages.length;
  const visiblePageCount = filteredPages.length;
  const isFilterActive = filter.trim().length > 0;
  const effectivePage =
    selectedPage != null && pages.some((memoryPage) => memoryPage.page === selectedPage)
      ? selectedPage
      : (pages[0]?.page ?? null);

  const pageResult = useSkippableBenchlingQuery<RoutineMemoryPageQuery, RoutineMemoryPageQueryVariables>(
    ROUTINE_MEMORY_PAGE_QUERY,
    {
      variables: {routineId, page: effectivePage ?? ''},
      skip: effectivePage == null,
      fetchPolicy: 'network-only',
    }
  );

  const handleSelectPage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const page = event.currentTarget.dataset.page;
    if (page != null) {
      setSelectedPage(page);
    }
  }, []);

  const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.currentTarget.value);
  }, []);

  let content: JSX.Element;
  if (
    pagesResult.type === QueryResultType.PENDING ||
    (effectivePage != null && pageResult.type === QueryResultType.PENDING)
  ) {
    content = (
      <Center role="status">
        <Stack align="center" spacing="xs">
          <Loader size="sm" />
          <Text size="sm" color="dimmed">
            Loading memory...
          </Text>
        </Stack>
      </Center>
    );
  } else if (pagesResult.type === QueryResultType.ERROR || pageResult.type === QueryResultType.ERROR) {
    content = (
      <Center role="alert">
        <Text size="sm">Unable to load this agent's memory.</Text>
      </Center>
    );
  } else if (pages.length === 0) {
    content = (
      <Center>
        <Text size="sm" color="dimmed">
          This agent has no memory yet.
        </Text>
      </Center>
    );
  } else if (
    effectivePage != null &&
    pageResult.type === QueryResultType.SUCCESS &&
    pageResult.data.routineMemoryPage != null
  ) {
    content = (
      <Stack spacing="md">
        <Text component="h5" className={classes.contentPath} fw={600}>
          {effectivePage}
        </Text>
        <Divider />
        <LLMMarkdownSection markdown={pageResult.data.routineMemoryPage.content} />
      </Stack>
    );
  } else {
    content = (
      <Center role="status">
        <Text size="sm" color="dimmed">
          This memory page is no longer available.
        </Text>
      </Center>
    );
  }

  return (
    <div aria-labelledby={MEMORY_VIEWER_PANEL_TITLE_ID} className={classes.panel} role="region">
      <Stack className={classes.header} spacing="md">
        <Group spacing="xs">
          <ActionIcon
            aria-label={`Back to ${agentName}`}
            autoFocus={true}
            onClick={onClose}
            size="sm"
            variant="ghost"
          >
            <BenchlingIcon icon="chevron-left" />
          </ActionIcon>
          <Text size="lg" fw={600}>
            {agentName}
          </Text>
        </Group>
        <Text component="h4" fw={600} id={MEMORY_VIEWER_PANEL_TITLE_ID} size="xl">
          Memory
        </Text>
      </Stack>
      <Divider />
      <Box className={classes.body}>
        {pages.length > 0 && (
          <Box component="nav" aria-label="Memory pages" className={classes.pageList}>
            <Stack spacing="md">
              <Stack className={classes.pageListControls} spacing="xs">
                <Text color="dimmed" size="xs">
                  {isFilterActive
                    ? `showing ${visiblePageCount} of ${loadedCount}`
                    : truncated
                      ? `showing ${loadedCount} of ${totalCount}`
                      : `${loadedCount} ${loadedCount === 1 ? 'page' : 'pages'}`}
                </Text>
                <TextInput
                  aria-label="Filter memory pages"
                  icon={<BenchlingIcon icon="search" />}
                  onChange={handleFilterChange}
                  placeholder="Filter memory"
                  size="xs"
                  value={filter}
                />
                {truncated && (
                  <Text color="dimmed" size="xs">
                    Only the first {loadedCount} of {totalCount} pages are shown. The filter searches only
                    these pages.
                  </Text>
                )}
              </Stack>
              {filteredPages.length === 0 ? (
                <Text color="dimmed" size="xs">
                  No matching pages
                </Text>
              ) : (
                <Stack spacing={4}>
                  {filteredPages.map((memoryPage) => (
                    <Button
                      key={memoryPage.page}
                      aria-label={memoryPage.page}
                      aria-pressed={effectivePage === memoryPage.page}
                      className={classes.pageButton}
                      classNames={{inner: classes.pageButtonInner, label: classes.pageButtonLabel}}
                      color="gray"
                      data-page={memoryPage.page}
                      onClick={handleSelectPage}
                      size="sm"
                      title={memoryPage.page}
                      variant={effectivePage === memoryPage.page ? 'light' : 'subtle'}
                    >
                      {memoryPage.page}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
        <Box component="section" aria-label={effectivePage ?? 'Memory content'} className={classes.content}>
          {content}
        </Box>
      </Box>
    </div>
  );
}
